import OnlineIndicator from "@features/presence/components/OnlineIndicator";
import { getProfileThumbnail } from "@shared/hooks/useProfile";
import theme from "@shared/utils/themeColors";
import React from "react";
import { useConversations } from "../context/ConversationsContext";
import { DirectMessagesProps } from "../types";

const DirectMessages: React.FC<DirectMessagesProps> = ({
  setMsgSidebarOpen,
  selectedConversation,
  setSelectedConversation,
  searchTerm,
}) => {
  const {
    conversations,
    loading: conversationsLoading,
    userProfiles,
    getOrCreateConversation,
  } = useConversations();

  return (
    <div className="mt-4">
      <div
        className={`text-xs font-semibold text-gray-400 dark: ${theme.text.muted}`}
      >
        Direct messages
      </div>
      <ul className="mb-6">
        {conversationsLoading ? (
          <div className="flex items-center justify-center h-20">
            <div
              className={`animate-spin rounded-full h-8 w-8 border-b-2 ${theme.border.brand}`}
            ></div>
          </div>
        ) : (
          // Filter users by name before mapping
          Object.values(userProfiles)
            .filter((otherUser) => {
              if (!searchTerm || searchTerm.trim() === "") return true;
              return otherUser.name
                .toLowerCase()
                .includes(searchTerm.toLowerCase());
            })
            .map((otherUser) => {
              // Select conversations of type 'direct' or without type but with 2 participants (compatibility with old data)
              const conversation = conversations.find(
                (conv) =>
                  (conv.type === "direct" ||
                    (!conv.type && conv.participants.length === 2)) &&
                  conv.participants.includes(otherUser.id)
              );

              return (
                <li className="-mx-2" key={otherUser.id}>
                  <button
                    className={`flex items-center justify-between w-full p-2 rounded-lg ${selectedConversation === conversation?.id
                      ? `bg-[linear-gradient(135deg,var(--tw-gradient-stops))] ${theme.bg.gradientPrimary}`
                      : "hover:bg-slate-50 dark:hover:bg-slate-800"
                      }`}
                    onClick={() => {
                      setMsgSidebarOpen(false);
                      if (conversation) {
                        setSelectedConversation(conversation.id);
                      } else {
                        getOrCreateConversation(otherUser.id).then(
                          (conversationId) => {
                            if (conversationId) {
                              setSelectedConversation(conversationId);
                            }
                          }
                        );
                      }
                    }}
                  >
                    <div className="flex items-center truncate">
                      <div className="relative mr-2 flex-shrink-0">
                        <img
                          className="w-8 h-8 rounded-full"
                          src={getProfileThumbnail(otherUser)}
                          width="32"
                          height="32"
                          alt={otherUser.name}
                        />
                        <OnlineIndicator
                          userId={otherUser.id}
                          className="absolute bottom-0 right-0"
                        />
                      </div>
                      <div>
                        <div className="truncate text-left">
                          <span
                            className={`text-sm font-medium ${theme.text.primary}`}
                          >
                            {otherUser.name}
                          </span>
                        </div>
                        {conversation?.lastMessage && (
                          <div className="text-sm text-left text-slate-500 dark:text-slate-400 truncate">
                            {conversation.lastMessage.content}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center ml-2">
                      {conversation?.unreadCount > 0 && (
                        <div
                          className={`text-xs inline-flex font-medium ${theme.bg.badge} ${theme.text.white} rounded-full text-center leading-5 px-2`}
                        >
                          {conversation?.unreadCount}
                        </div>
                      )}
                    </div>
                  </button>
                </li>
              );
            })
        )}
      </ul>
    </div>
  );
};

export default DirectMessages;
